import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import { Indonesian } from 'flatpickr/dist/l10n/id.js';
import { toast } from 'react-toastify';
import {
  BtnBold,
  BtnItalic,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  Separator,
  createDropdown,
} from 'react-simple-wysiwyg';

// Dropdown "Highlight" ala desain acuan — memakai execCommand hiliteColor.
const BtnHighlight = createDropdown('Highlight', [
  ['Yellow', 'hiliteColor', '#fff59d'],
  ['Green', 'hiliteColor', '#b9f6ca'],
  ['Cyan', 'hiliteColor', '#a7ffeb'],
  ['Pink', 'hiliteColor', '#ffcdd2'],
  ['None', 'hiliteColor', 'transparent'],
]);

import ConfirmModal from '../../../../../Components/crude/ConfirmModal';
import ActionButton from '../../../../../Components/Common/ActionButton';
import AddCrudeModal from './AddCrudeModal';
import { CONTRACT_TYPES, REQUIREMENT_SECTIONS } from '../constants';
import * as api from '../planningApi';
import { toIsoDate, formatDate } from '../../formatters';

// Rich-text field bersama untuk clause Summary Note & Notes for Cargo
// Requirement. Nilainya HTML string di field yang sama yang dikirim API sebagai
// string biasa (`note` / `cargoNotes`) — kontrak tidak berubah. Editor:
// react-simple-wysiwyg, MENIRU pola TenderOpening components/RequirementTab.js
// (tanpa badge "Powered by CKEditor"). Didefinisikan di level modul supaya
// identitas komponennya stabil antar render (editor tidak ikut re-mount saat
// parent re-render, caret tidak loncat).
const RichNote = ({
  value,
  onChange,
  readOnly,
  templates = [],
  onInsertTemplate,
}) => {
  if (readOnly) {
    return (
      <div
        className='form-control bg-light'
        style={{ minHeight: 96, overflow: 'auto' }}
        dangerouslySetInnerHTML={{
          __html: value || '<p class="text-muted mb-0">-</p>',
        }}
      />
    );
  }
  return (
    <EditorProvider>
      <Editor
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ height: '200px', overflowY: 'auto' }}
        className='form-control bg-white'
      >
        <Toolbar>
          <BtnBold />
          <BtnItalic />
          <BtnBulletList />
          <BtnClearFormatting />
          <BtnLink />
          <BtnNumberedList />
          <BtnRedo />
          <BtnStrikeThrough />
          <BtnStyles />
          <BtnUnderline />
          <BtnHighlight />
          <BtnUndo />
          <Separator />
          {/* "-Add Template-" DI DALAM toolbar (desain acuan): sisipkan isi
              Clausa Information milik kategori clause ini ke editor. */}
          {templates.length > 0 && onInsertTemplate && (
            <select
              className='form-select form-select-sm'
              style={{ marginLeft: 'auto', maxWidth: 220 }}
              value=''
              onChange={e => {
                const tpl = templates.find(t => t.id === e.target.value);
                if (tpl) onInsertTemplate(tpl);
              }}
            >
              <option value=''>-Add Template-</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          )}
        </Toolbar>
      </Editor>
    </EditorProvider>
  );
};

const emptyCargoDraft = {
  cargoCategoryId: '',
  ot: '',
  otOption: "Buyer's Option", // "Buyer's Option" | "Seller's Option"
  volume: '',
  ddrFrom: null,
  ddrTo: null,
  ddrNotes: '',
  incotermIds: [],
  incotermsNotes: '',
  cduId: '',
  // UI-only: Delivery (Refinery Unit) filter for the CDU select — never sent
  // to the API (addCargo still sends cduId alone).
  plantId: '',
};

// Main Requirement tab. Contract type / periods / clauses / other terms are
// edited locally and persisted in one shot via SaveMainRequirement (the parent's
// Save buttons). Cargo and Crude, by contrast, are their own endpoints and are
// persisted immediately here — after each we pull fresh cargos from the server
// (`refresh`) so the ids needed for the crude lookup/mutations are real.
const RequirementTab = ({
  planningId,
  detail,
  setDetail,
  refresh,
  readOnly = false,
  isHardRecall = false,
}) => {
  const req = detail.requirement;

  // Contract (type + both periods) is frozen once Hard Recall reopens a Planning.
  // As of 2026-07-23 the server enforces this by IGNORING those three fields on
  // SaveMainRequirement rather than rejecting them — the stored values simply
  // never change and no error comes back (contract section 6; the earlier
  // equality check that 422'd on any difference is gone). Disabling the inputs is
  // therefore a UX guarantee, not a workaround: without it the fields would look
  // editable and silently discard every change. Cargo / Clause / Other Terms in
  // the same request are still saved normally, so this locks the Contract
  // section only — not the whole tab.
  const contractLocked = readOnly || isHardRecall;
  const [draft, setDraft] = useState(emptyCargoDraft);
  const [cargoModalOpen, setCargoModalOpen] = useState(false); // Add Cargo popup
  const [openClauses, setOpenClauses] = useState({});
  const [deleteCargoRow, setDeleteCargoRow] = useState(null); // cargo obj
  const [busy, setBusy] = useState(false);

  // Master lookups (all live; CargoCategories is on the ProHydro host).
  const [cargoCategories, setCargoCategories] = useState([]);
  const [cduList, setCduList] = useState([]);
  const [incoterms, setIncoterms] = useState([]);

  const [expandedCargo, setExpandedCargo] = useState(null); // cargo id
  const [crudeModalIdx, setCrudeModalIdx] = useState(null); // cargo index
  const [deleteCrude, setDeleteCrude] = useState(null); // { cargo, row }

  // Clause toolbar (Load Standard / Add Eksisting / Add New) + per-clause delete.
  const [clauseTemplates, setClauseTemplates] = useState([]); // lookup rows
  const [existingClauseModal, setExistingClauseModal] = useState(false);
  // { [templateId]: { checked: bool, mandatory: bool } }
  const [clauseSelection, setClauseSelection] = useState({});
  const [clauseSearch, setClauseSearch] = useState('');
  const [newClauseModal, setNewClauseModal] = useState(false);
  const [newClauseDraft, setNewClauseDraft] = useState({
    title: '',
    mandatory: false,
    note: '',
  });
  const [deleteClauseRow, setDeleteClauseRow] = useState(null); // clause obj
  // Opsi "-Add Template-" per clause: { [clauseId]: [{id,title,contents}] }.
  // Dimuat lazily saat accordion clause ber-kategori dibuka.
  const [clauseTplOptions, setClauseTplOptions] = useState({});

  useEffect(() => {
    if (readOnly) return; // detail (read-only) never adds cargo, skip lookups
    api
      .lookupCargoCategories()
      .then(setCargoCategories)
      .catch(e => toast.error(`Failed to load Cargo Category: ${e}`));
    api
      .getCduList()
      .then(setCduList)
      .catch(e => toast.error(`Failed to load CDU list: ${e}`));
    api
      .lookupIncoterm()
      .then(setIncoterms)
      .catch(e => toast.error(`Failed to load Incoterm: ${e}`));
  }, [readOnly]);

  // Pull fresh cargos from the server without clobbering unsaved contract/
  // clause edits held only in local state.
  const reloadCargos = useCallback(async () => {
    const fresh = await refresh();
    setDetail(prev => ({
      ...prev,
      requirement: { ...prev.requirement, cargos: fresh.requirement.cargos },
    }));
  }, [refresh, setDetail]);

  // Pull fresh clauses from the server after add/delete, KEEPING local unsaved
  // edits (mandatory switch + summary note) on clauses already in this tab —
  // those are only persisted later via SaveMainRequirement.
  const reloadClauses = useCallback(async () => {
    const fresh = await refresh();
    setDetail(prev => {
      const local = prev.requirement.clauses || [];
      const merged = (fresh.requirement.clauses || []).map(fc => {
        const l = local.find(c => c.clauseId === fc.clauseId);
        return l
          ? { ...fc, mandatory: l.mandatory, summernote: l.summernote }
          : fc;
      });
      return {
        ...prev,
        requirement: { ...prev.requirement, clauses: merged },
      };
    });
  }, [refresh, setDetail]);

  // Merge a patch into detail.requirement (local only — saved via the parent).
  const patchReq = patch =>
    setDetail(prev => ({
      ...prev,
      requirement: { ...prev.requirement, ...patch },
    }));

  // ---- Contract ------------------------------------------------------------
  const renderContract = () => (
    <>
      {isHardRecall && !readOnly && (
        <div className='alert alert-warning d-flex align-items-start mb-3'>
          <i className='ri-lock-2-line fs-16 me-2 mt-1'></i>
          <div>
            <strong>Contract is locked by Hard Recall.</strong> Contract Type,
            Start Period and End Period cannot be changed while this Planning is
            being revised — the server keeps the stored values regardless of
            what is sent. Cargo Specification and Clause Detail remain editable.
          </div>
        </div>
      )}
      <Row className='g-3 align-items-start'>
        {CONTRACT_TYPES.map((ct, idx) => (
          <Col lg={2} sm={4} key={ct.value}>
            <Label className='form-label'>
              {idx === 0 ? (
                <>
                  Contract Type <span className='text-danger'>*</span>
                </>
              ) : (
                ' '
              )}
            </Label>
            <div className='form-check card-radio'>
              <Input
                id={`contractType-${ct.value}`}
                name='contractType'
                type='radio'
                value={ct.value}
                className='form-check-input'
                checked={req.contractType === ct.value}
                disabled={contractLocked}
                onChange={() => patchReq({ contractType: ct.value })}
              />
              <Label
                className='form-check-label'
                for={`contractType-${ct.value}`}
              >
                <span className='fs-16 me-2'>
                  <i className='ri-file-list-2-line align-middle'></i>
                </span>
                <span className='fs-15 mb-1 text-wrap'>
                  {ct.label.toUpperCase()}
                </span>
              </Label>
            </div>
          </Col>
        ))}

        <Col lg={2} sm={6}>
          <Label className='form-label'>
            Start Period <span className='text-danger'>*</span>
          </Label>
          <Flatpickr
            value={req.startPeriod ? new Date(req.startPeriod) : null}
            disabled={contractLocked}
            options={{
              altInput: true,
              altFormat: 'M Y',
              dateFormat: 'Y-m',
              locale: Indonesian,
              clickOpens: !contractLocked,
              allowInput: !contractLocked,
              plugins: [
                new monthSelectPlugin({
                  shorthand: false,
                  dateFormat: 'Y-m',
                  altFormat: 'F Y',
                  theme: 'light',
                }),
              ],
            }}
            className='form-control bg-light border-1'
            placeholder='Select month'
            onChange={([date]) => {
              if (contractLocked) return;
              patchReq({ startPeriod: toIsoDate(date) });
            }}
          />
        </Col>
        <Col lg={2} sm={6}>
          <Label className='form-label'>
            End Period <span className='text-danger'>*</span>
          </Label>
          <Flatpickr
            value={req.endPeriod ? new Date(req.endPeriod) : null}
            disabled={contractLocked}
            options={{
              altInput: true,
              altFormat: 'M Y',
              dateFormat: 'Y-m',
              locale: Indonesian,
              clickOpens: !contractLocked,
              allowInput: !contractLocked,
              minDate: req.startPeriod ? new Date(req.startPeriod) : undefined,
              plugins: [
                new monthSelectPlugin({
                  shorthand: false,
                  dateFormat: 'Y-m',
                  altFormat: 'F Y',
                  theme: 'light',
                }),
              ],
            }}
            className='form-control bg-light border-1'
            placeholder='Select month'
            onChange={([date]) => {
              if (contractLocked) return;
              const iso = toIsoDate(date);
              if (
                iso &&
                req.startPeriod &&
                new Date(iso) <= new Date(req.startPeriod)
              ) {
                toast.error('End Period must be after Start Period');
                return;
              }
              patchReq({ endPeriod: iso });
            }}
          />
        </Col>
      </Row>
    </>
  );

  // ---- Cargo Specification -------------------------------------------------
  const toggleIncoterm = id => {
    setDraft(prev => {
      const has = prev.incotermIds.includes(id);
      return {
        ...prev,
        incotermIds: has
          ? prev.incotermIds.filter(i => i !== id)
          : [...prev.incotermIds, id],
      };
    });
  };

  const validateCargoDraft = () => {
    const missing = [];
    if (!draft.cargoCategoryId) missing.push('Cargo Type');
    if (draft.ot === '' || draft.ot === null) {
      missing.push('Operational Tolerance (%)');
    } else if (Number(draft.ot) < 0 || Number(draft.ot) > 100) {
      toast.error('Operational Tolerance (%) must be between 0 and 100');
      return false;
    }
    if (!draft.ddrFrom || !draft.ddrTo) missing.push('DDR');
    if (
      draft.ddrFrom &&
      draft.ddrTo &&
      new Date(draft.ddrTo) < new Date(draft.ddrFrom)
    ) {
      toast.error('DDR: Date To cannot be earlier than Date From');
      return false;
    }
    if (!draft.cduId) missing.push('Unit (CDU)');
    if (draft.incotermIds.length === 0) missing.push('Incoterms');
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleAddCargo = async () => {
    if (!validateCargoDraft()) return;
    setBusy(true);
    try {
      await api.addCargo(planningId, {
        cargoCategoryId: draft.cargoCategoryId,
        otPercentage: Number(draft.ot),
        operationalToleranceOption: draft.otOption,
        volumeKbbl: draft.volume === '' ? undefined : Number(draft.volume),
        ddrFrom: toIsoDate(draft.ddrFrom),
        ddrTo: toIsoDate(draft.ddrTo),
        ddrNotes: draft.ddrNotes,
        cduId: draft.cduId,
        incotermsNotes: draft.incotermsNotes,
        incotermIds: draft.incotermIds,
      });
      await reloadCargos();
      setDraft(emptyCargoDraft);
      setCargoModalOpen(false);
      toast.success('Cargo added');
    } catch (error) {
      toast.error(`Failed to add cargo: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteCargo = async () => {
    const cargo = deleteCargoRow;
    setDeleteCargoRow(null);
    setBusy(true);
    try {
      await api.deleteCargo(planningId, cargo.id);
      await reloadCargos();
      toast.success('Cargo removed');
    } catch (error) {
      toast.error(`Failed to remove cargo: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  // ---- Clause actions -------------------------------------------------------
  const handleLoadStandardClauses = async () => {
    setBusy(true);
    try {
      // Lookup mengembalikan kategori aktif + non-aktif; "standard" = AKTIF
      // saja (yang non-aktif hanya lewat Add Eksisting Clause).
      const templates = (await api.lookupClauseFromTemplate(planningId)).filter(
        t => t.isActive
      );
      if (!templates.length) {
        toast.info('All standard clauses are already in this Planning');
        return;
      }
      await api.addClausesFromTemplate(
        planningId,
        templates.map(t => ({
          templateInformationCategoryId: t.id,
          isMandatoryForDmut: false,
        }))
      );
      await reloadClauses();
      toast.success(`${templates.length} standard clause(s) loaded`);
    } catch (error) {
      toast.error(`Failed to load standard clauses: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const openExistingClauseModal = async () => {
    setBusy(true);
    try {
      const templates = await api.lookupClauseFromTemplate(planningId);
      if (!templates.length) {
        toast.info('All master clauses are already in this Planning');
        return;
      }
      setClauseTemplates(templates);
      setClauseSelection({});
      setClauseSearch('');
      setExistingClauseModal(true);
    } catch (error) {
      toast.error(`Failed to load master clauses: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const selectedClauseItems = Object.entries(clauseSelection)
    .filter(([, v]) => v.checked)
    .map(([id, v]) => ({
      templateInformationCategoryId: id,
      isMandatoryForDmut: v.mandatory,
    }));

  const handleAddExistingClauses = async () => {
    if (!selectedClauseItems.length) {
      toast.error('Select at least 1 clause');
      return;
    }
    setBusy(true);
    try {
      await api.addClausesFromTemplate(planningId, selectedClauseItems);
      await reloadClauses();
      setExistingClauseModal(false);
      toast.success(`${selectedClauseItems.length} clause(s) added`);
    } catch (error) {
      toast.error(`Failed to add clauses: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const handleAddNewClause = async () => {
    if (!newClauseDraft.title.trim()) {
      toast.error('Clause title is required');
      return;
    }
    if (!newClauseDraft.note.trim()) {
      toast.error('Clause content is required');
      return;
    }
    setBusy(true);
    try {
      await api.addClause(planningId, {
        title: newClauseDraft.title.trim(),
        isMandatoryForDmut: newClauseDraft.mandatory,
        note: newClauseDraft.note,
      });
      await reloadClauses();
      setNewClauseModal(false);
      toast.success('Clause added');
    } catch (error) {
      toast.error(`Failed to add clause: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteClause = async () => {
    const clause = deleteClauseRow;
    setDeleteClauseRow(null);
    setBusy(true);
    try {
      await api.deleteClause(planningId, clause.clauseId);
      await reloadClauses();
      toast.success('Clause removed');
    } catch (error) {
      toast.error(`Failed to remove clause: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  // OK on the Add popup — POST each selected crude, then refresh the cargo.
  const handleAddCrudes = async selected => {
    const cargo = req.cargos[crudeModalIdx];
    if (!cargo) return;
    setCrudeModalIdx(null);
    setBusy(true);
    try {
      for (const s of selected) {
        // eslint-disable-next-line no-await-in-loop
        await api.addCargoCrude(planningId, cargo.id, {
          implementationCrudeId: s.implementationCrudeId,
          maxCapacity: Number(s.maxCapacity),
          maxCapacityNote: s.remarkMaxCapacity,
          isViaCIBOnly: !!s.isViaCibOnly,
          blendingCrude1:
            s.blending1 === '' || s.blending1 == null
              ? undefined
              : Number(s.blending1),
          blendingCrude2:
            s.blending2 === '' || s.blending2 == null
              ? undefined
              : Number(s.blending2),
        });
      }
      await reloadCargos();
      toast.success(
        `${selected.length} Crude / Condensat added to ${cargo.cargoType}`
      );
    } catch (error) {
      toast.error(`Failed to add Crude / Condensat: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteCrude = async () => {
    const { cargo, row } = deleteCrude;
    setDeleteCrude(null);
    setBusy(true);
    try {
      await api.deleteCargoCrude(planningId, cargo.id, row.id);
      await reloadCargos();
      toast.success('Crude / Condensat removed');
    } catch (error) {
      toast.error(`Failed to remove Crude / Condensat: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  // Panel shown when a cargo row is expanded: its Crude / Condensat list plus
  // the Add button that opens the master implementation-crude lookup popup.
  const renderCrudeList = (cargo, cargoIdx) => {
    const crudes = cargo.crudes || [];
    return (
      <>
        <div className='d-flex align-items-center mb-2'>
          <h6 className='mb-0 flex-grow-1'>
            <i className='ri-oil-line align-middle me-2'></i>
            Crude / Condensat
            <span className='text-muted fw-normal fs-12 ms-2'>
              {cargo.cargoType}
              {cargo.unit ? ` @ ${cargo.unit}` : ''}
            </span>
          </h6>
          {!readOnly && (
            <ActionButton
              variant='secondary'
              size='sm'
              icon='ri-add-box-line'
              disabled={busy}
              onClick={() => setCrudeModalIdx(cargoIdx)}
            >
              Add Crude / Condensat
            </ActionButton>
          )}
        </div>

        {/* Tinggi dibatasi ±5 baris — sisanya scroll, header tetap terlihat. */}
        <div
          className='table-responsive bg-white border rounded'
          style={{ maxHeight: 265, overflowY: 'auto' }}
        >
          <Table className='table-sm align-middle mb-0'>
            <thead
              className='table-light'
              style={{ position: 'sticky', top: 0, zIndex: 1 }}
            >
              <tr>
                <th>Crude/Condensat</th>
                <th style={{ width: '130px' }}>Max Capacity</th>
                <th style={{ width: '220px' }}>Remark Max Capacity</th>
                <th style={{ width: '110px' }} className='text-center'>
                  Is Via CIB Only ?
                </th>
                <th style={{ width: '110px' }}>% Bleeding Crude (1)</th>
                <th style={{ width: '110px' }}>% Bleeding Crude (2)</th>
                {!readOnly && (
                  <th style={{ width: '90px' }} className='text-center'>
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {crudes.length === 0 ? (
                <tr>
                  <td
                    colSpan={readOnly ? 6 : 7}
                    className='text-center text-muted py-3'
                  >
                    No Crude / Condensat added yet
                  </td>
                </tr>
              ) : (
                crudes.map(row => (
                  <tr key={row.id}>
                    <td>{row.crudeName}</td>
                    <td>{row.maxCapacity ?? '-'}</td>
                    <td>{row.remarkMaxCapacity || '-'}</td>
                    <td className='text-center'>
                      {row.isViaCibOnly ? 'Yes' : 'No'}
                    </td>
                    <td>{row.bleeding1 ?? '-'}</td>
                    <td>{row.bleeding2 ?? '-'}</td>
                    {!readOnly && (
                      <td className='text-center'>
                        <ActionButton
                          variant='icon'
                          danger
                          size='sm'
                          icon='ri-delete-bin-line'
                          label='Delete'
                          confirm={false}
                          disabled={busy}
                          onClick={() => setDeleteCrude({ cargo, row })}
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  // ---- Plant → CDU cascade (UI-only) ----------------------------------------
  // GetListCDU items carry plantId/plantName alongside cduName, so the
  // Delivery (Refinery Unit) select just filters the CDU options client-side.
  // The addCargo payload still sends cduId alone — no contract change. If the
  // items ever come back without plant info, the plant select disables itself
  // and the CDU select falls back to the flat, unfiltered list.
  const plantOptions = cduList.reduce((acc, u) => {
    const name = u.plantName || u.plant || '';
    const key = String(u.plantId ?? name);
    if (name && !acc.some(p => p.id === key)) acc.push({ id: key, name });
    return acc;
  }, []);
  const cduOptions = plantOptions.length
    ? draft.plantId
      ? cduList.filter(
          u =>
            String(u.plantId ?? u.plantName ?? u.plant ?? '') === draft.plantId
        )
      : []
    : cduList;

  const renderCargo = () => (
    <>
      {!readOnly && (
        <>
          <div className='text-end mb-2'>
            <ActionButton
              variant='secondary'
              icon='ri-add-box-line'
              onClick={() => {
                setDraft(emptyCargoDraft);
                setCargoModalOpen(true);
              }}
            >
              Add New Cargo
            </ActionButton>
          </div>

          <Modal
            isOpen={cargoModalOpen}
            toggle={() => setCargoModalOpen(false)}
            size='lg'
            centered
          >
            <ModalHeader toggle={() => setCargoModalOpen(false)}>
              Add New Cargo/Segmen Kebutuhan
            </ModalHeader>
            <ModalBody>
              <div className='mb-3'>
                <Label className='form-label'>
                  Cargo Type <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='select'
                  value={draft.cargoCategoryId}
                  onChange={e =>
                    setDraft({ ...draft, cargoCategoryId: e.target.value })
                  }
                >
                  <option value=''>Select Cargo Type</option>
                  {cargoCategories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.text}
                    </option>
                  ))}
                </Input>
              </div>

              <Row>
                <Col sm={6}>
                  <div className='mb-3'>
                    <Label className='form-label'>Parcel Size (Kbbl)</Label>
                    <div className='input-group'>
                      <Input
                        type='number'
                        value={draft.volume}
                        onChange={e =>
                          setDraft({ ...draft, volume: e.target.value })
                        }
                      />
                      <span className='input-group-text fw-semibold'>Kbbl</span>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className='mb-3'>
                    <Label className='form-label'>
                      Operational Tolerance (%){' '}
                      <span className='text-danger'>*</span>
                    </Label>
                    <div className='input-group'>
                      <span className='input-group-text'>(±)</span>
                      <Input
                        type='number'
                        min={0}
                        max={100}
                        value={draft.ot}
                        onChange={e => {
                          // Percentage — clamp to 0..100 while typing.
                          let v = e.target.value;
                          if (v !== '') {
                            const n = Number(v);
                            if (!Number.isNaN(n)) {
                              v = String(Math.min(100, Math.max(0, n)));
                            }
                          }
                          setDraft({ ...draft, ot: v });
                        }}
                      />
                      <Input
                        type='select'
                        style={{ maxWidth: 150 }}
                        value={draft.otOption}
                        onChange={e =>
                          setDraft({ ...draft, otOption: e.target.value })
                        }
                        aria-label='Operational Tolerance option'
                      >
                        <option value="Buyer's Option">
                          Buyer&apos;s Option
                        </option>
                        <option value="Seller's Option">
                          Seller&apos;s Option
                        </option>
                      </Input>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className='mb-3'>
                <Label className='form-label'>
                  Incoterms <span className='text-danger'>*</span>
                </Label>
                <div className='border rounded p-2 d-flex flex-wrap gap-3'>
                  {incoterms.map(t => (
                    <div
                      className='form-check form-check-inline m-0'
                      key={t.id}
                    >
                      <Input
                        type='checkbox'
                        id={`incoterm-${t.id}`}
                        className='form-check-input'
                        checked={draft.incotermIds.includes(t.id)}
                        onChange={() => toggleIncoterm(t.id)}
                      />
                      <Label
                        for={`incoterm-${t.id}`}
                        className='form-check-label'
                      >
                        {t.text}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className='mb-3'>
                <Label className='form-label'>Incoterms Notes</Label>
                <Input
                  type='text'
                  placeholder='Freetext Notes...'
                  value={draft.incotermsNotes}
                  onChange={e =>
                    setDraft({ ...draft, incotermsNotes: e.target.value })
                  }
                />
              </div>

              <Row>
                <Col sm={6}>
                  <div className='mb-3'>
                    <Label className='form-label'>
                      Delivery (Refinery Unit)
                    </Label>
                    <Input
                      type='select'
                      value={draft.plantId}
                      disabled={plantOptions.length === 0}
                      onChange={e =>
                        setDraft({
                          ...draft,
                          plantId: e.target.value,
                          cduId: '',
                        })
                      }
                    >
                      <option value=''>
                        -- Pilih Refinery Unit (Plant) --
                      </option>
                      {plantOptions.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className='mb-3'>
                    <Label className='form-label'>
                      Unit (CDU) <span className='text-danger'>*</span>
                    </Label>
                    <Input
                      type='select'
                      value={draft.cduId}
                      disabled={plantOptions.length > 0 && !draft.plantId}
                      onChange={e =>
                        setDraft({ ...draft, cduId: e.target.value })
                      }
                    >
                      <option value=''>-- Pilih Unit (CDU) --</option>
                      {cduOptions.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.cduName || u.text}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              </Row>

              <div className='mb-3'>
                <Label className='form-label'>
                  Delivery Date Range (DDR){' '}
                  <span className='text-danger'>*</span>
                </Label>
                <Row>
                  <Col sm={6}>
                    <DatePicker
                      selected={draft.ddrFrom ? new Date(draft.ddrFrom) : null}
                      onChange={d => setDraft({ ...draft, ddrFrom: d })}
                      dateFormat='dd/MM/yyyy'
                      className='form-control'
                      placeholderText='dd/mm/yyyy'
                    />
                  </Col>
                  <Col sm={6}>
                    <DatePicker
                      selected={draft.ddrTo ? new Date(draft.ddrTo) : null}
                      onChange={d => setDraft({ ...draft, ddrTo: d })}
                      dateFormat='dd/MM/yyyy'
                      minDate={
                        draft.ddrFrom ? new Date(draft.ddrFrom) : undefined
                      }
                      className='form-control'
                      placeholderText='dd/mm/yyyy'
                    />
                  </Col>
                </Row>
              </div>

              <div className='mb-1'>
                <Label className='form-label'>DDR Notes</Label>
                <Input
                  type='textarea'
                  rows={3}
                  placeholder='Freetext Notes...'
                  value={draft.ddrNotes}
                  onChange={e =>
                    setDraft({ ...draft, ddrNotes: e.target.value })
                  }
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <ActionButton
                variant='secondary'
                icon='ri-close-line'
                disabled={busy}
                onClick={() => setCargoModalOpen(false)}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                icon='ri-add-box-line'
                disabled={busy}
                onClick={handleAddCargo}
              >
                Add Cargo
              </ActionButton>
            </ModalFooter>
          </Modal>
        </>
      )}

      <Card className='border shadow-none mt-2'>
        <CardHeader className='fw-semibold bg-light'>
          Cargo Requirement
        </CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Cargo Type</th>
                  <th className='text-end'>Parcel Size</th>
                  <th className='text-end'>OT (%)</th>
                  <th>Incoterms</th>
                  <th>Incoterms Notes</th>
                  <th>Delivery</th>
                  <th>DDR</th>
                  <th>DDR Notes</th>
                  {!readOnly && <th className='text-center'>Action</th>}
                </tr>
              </thead>
              <tbody>
                {req.cargos.length === 0 ? (
                  <tr>
                    <td
                      colSpan={readOnly ? 9 : 10}
                      className='text-center text-muted py-3'
                    >
                      No cargo added yet
                    </td>
                  </tr>
                ) : (
                  req.cargos.map((c, idx) => {
                    const open = expandedCargo === c.id;
                    return (
                      <React.Fragment key={c.id}>
                        <tr>
                          <td className='text-center'>
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-ghost-secondary'
                              title={open ? 'Collapse' : 'Expand'}
                              aria-expanded={open}
                              aria-label={`Toggle Crude / Condensat for ${c.cargoType}`}
                              onClick={() =>
                                setExpandedCargo(open ? null : c.id)
                              }
                            >
                              <i
                                className={`ri-arrow-${open ? 'down' : 'right'}-s-line fs-16`}
                              ></i>
                            </button>
                          </td>
                          <td className='fw-medium'>{c.cargoType}</td>
                          <td className='text-end'>
                            {c.volume != null && c.volume !== ''
                              ? `${c.volume} Kbbl`
                              : '-'}
                          </td>
                          <td className='text-end'>
                            {c.ot != null && c.ot !== '' ? `±${c.ot}%` : '-'}
                            {c.otOption && (
                              <small className='text-muted d-block'>
                                {c.otOption}
                              </small>
                            )}
                          </td>
                          <td>
                            {(c.incoterms || []).length
                              ? c.incoterms.map(t => (
                                  <span
                                    key={t}
                                    className='badge bg-info-subtle text-info me-1'
                                  >
                                    {t}
                                  </span>
                                ))
                              : '-'}
                          </td>
                          <td>{c.incotermsNotes || '-'}</td>
                          <td>
                            {c.plant} - {c.unit}
                          </td>
                          <td>
                            {formatDate(c.ddrFrom)} – {formatDate(c.ddrTo)}
                          </td>
                          <td>{c.ddrNotes || '-'}</td>
                          {!readOnly && (
                            <td className='text-center'>
                              <ActionButton
                                variant='icon'
                                danger
                                size='sm'
                                icon='ri-delete-bin-line'
                                label='Delete'
                                confirm={false}
                                disabled={busy}
                                onClick={() => setDeleteCargoRow(c)}
                              />
                            </td>
                          )}
                        </tr>
                        {open && (
                          <tr>
                            <td
                              colSpan={readOnly ? 9 : 10}
                              className='bg-light p-3'
                            >
                              {renderCrudeList(c, idx)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Card className='border shadow-none mt-3'>
        <CardHeader className='fw-semibold bg-light'>
          Notes for Cargo Requirement
        </CardHeader>
        <CardBody>
          <RichNote
            readOnly={readOnly}
            value={req.cargoNotes}
            onChange={html => patchReq({ cargoNotes: html })}
          />
        </CardBody>
      </Card>
    </>
  );

  // ---- Clause Detail -------------------------------------------------------
  const renderClause = () => (
    <>
      {!readOnly && (
        <div className='d-flex flex-wrap justify-content-end gap-2 mb-3'>
          <ActionButton
            variant='secondary'
            icon='ri-download-2-line'
            disabled={busy}
            onClick={handleLoadStandardClauses}
          >
            Load Standard Clauses
          </ActionButton>
          <ActionButton
            variant='secondary'
            icon='ri-add-line'
            disabled={busy}
            onClick={openExistingClauseModal}
          >
            Add Eksisting Clause
          </ActionButton>
          <ActionButton
            variant='secondary'
            icon='ri-add-line'
            disabled={busy}
            onClick={() => {
              setNewClauseDraft({ title: '', mandatory: false, note: '' });
              setNewClauseModal(true);
            }}
          >
            Add New Clause Information
          </ActionButton>
        </div>
      )}
      {req.clauses.map((c, idx) => {
        const open = !!openClauses[c.clauseId];
        return (
          <Card className='border mb-2' key={c.clauseId}>
            <CardHeader
              role='button'
              className='d-flex align-items-center justify-content-between py-2'
              onClick={() => {
                setOpenClauses(prev => ({ ...prev, [c.clauseId]: !open }));
                // Lazily load opsi "-Add Template-" milik kategori clause ini.
                if (
                  !open &&
                  c.templateCategoryId &&
                  !clauseTplOptions[c.clauseId]
                ) {
                  api
                    .getClauseTemplates(planningId, c.clauseId)
                    .then(rows =>
                      setClauseTplOptions(prev => ({
                        ...prev,
                        [c.clauseId]: rows,
                      }))
                    )
                    .catch(() => {});
                }
              }}
            >
              <span className='fw-semibold'>{c.title}</span>
              <div className='d-flex align-items-center gap-2'>
                {!readOnly && (
                  <ActionButton
                    variant='icon'
                    danger
                    size='sm'
                    icon='ri-delete-bin-line'
                    label='Delete clause'
                    confirm={false}
                    disabled={busy}
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteClauseRow(c);
                    }}
                  />
                )}
                <i
                  className={`ri-arrow-${open ? 'up' : 'down'}-s-line fs-20`}
                ></i>
              </div>
            </CardHeader>
            {open && (
              <CardBody>
                <div className='form-check form-switch mb-3'>
                  <Input
                    type='checkbox'
                    role='switch'
                    className='form-check-input'
                    id={`mandatory-${c.clauseId}`}
                    checked={!!c.mandatory}
                    disabled={readOnly}
                    onChange={e => {
                      const clauses = [...req.clauses];
                      clauses[idx] = { ...c, mandatory: e.target.checked };
                      patchReq({ clauses });
                    }}
                  />
                  <Label
                    for={`mandatory-${c.clauseId}`}
                    className='form-check-label'
                  >
                    Mandatory clause for Registered Supplier to fulfill
                  </Label>
                </div>
                <Label className='form-label'>
                  Summary Note <span className='text-danger'>*</span>
                </Label>
                <RichNote
                  readOnly={readOnly}
                  value={c.summernote}
                  templates={
                    c.templateCategoryId
                      ? clauseTplOptions[c.clauseId] || []
                      : []
                  }
                  onInsertTemplate={tpl => {
                    const clauses = [...req.clauses];
                    const current = clauses[idx].summernote || '';
                    clauses[idx] = {
                      ...clauses[idx],
                      summernote: current
                        ? current + tpl.contents
                        : tpl.contents,
                    };
                    patchReq({ clauses });
                  }}
                  onChange={html => {
                    const clauses = [...req.clauses];
                    clauses[idx] = { ...c, summernote: html };
                    patchReq({ clauses });
                  }}
                />
              </CardBody>
            )}
          </Card>
        );
      })}
      {req.clauses.length === 0 && (
        <p className='text-muted fst-italic mb-0'>
          No clauses were seeded for this Jenis Pengadaan.
        </p>
      )}
    </>
  );

  // Other Terms section removed (2026-09-01): free-form notes now live in
  // "Notes for Cargo Requirement" (cargoNotes). `additionalNotes` is still
  // loaded and passed through on save so existing plannings keep their data
  // (the BE field OtherTermsNotes also feeds Procurement/TenderOpening views).

  const sectionRenderers = {
    contract: renderContract,
    cargo: renderCargo,
    clause: renderClause,
  };

  const sectionMeta = {
    contract: {
      icon: 'ri-file-list-2-line',
      desc: 'Choose the contract type and set its period.',
    },
    cargo: {
      icon: 'ri-ship-line',
      desc: 'Add cargo specification and delivery terms.',
    },
    clause: {
      icon: 'ri-article-line',
      desc: 'Review and set the clause details.',
    },
  };

  const scrollToSection = id =>
    document
      .getElementById(`req-sec-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div>
      <Row>
        <Col sm={12}>
          <div className='d-flex align-items-center mb-2'>
            <div className='flex-grow-1'>
              <h5 className='mb-1'>
                <i className='ri-file-list-3-line me-2'></i>
                Requirement Information
              </h5>
              <p className='text-muted mb-2 fs-12'>
                Complete all requirement sections below, then save as draft or
                save &amp; complete.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      <div className='d-flex align-items-start gap-3'>
        {/* Left section navigation (desktop only) */}
        <div className='req-nav d-none d-lg-block'>
          <div className='req-nav-title'>Sections</div>
          {REQUIREMENT_SECTIONS.map(s => (
            <button
              key={s.id}
              type='button'
              className='req-nav-link'
              onClick={() => scrollToSection(s.id)}
            >
              <i className={`${sectionMeta[s.id].icon} me-2`}></i>
              {s.label}
            </button>
          ))}
        </div>

        <div className='flex-grow-1' style={{ minWidth: 0 }}>
          <Card className='border shadow-sm'>
            <CardBody>
              {REQUIREMENT_SECTIONS.map((s, idx) => (
                <div
                  key={s.id}
                  className={idx > 0 ? 'mt-4 pt-3 border-top' : ''}
                >
                  <p
                    id={`req-sec-${s.id}`}
                    className='mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                  >
                    {s.label}
                  </p>
                  <p className='text-muted mb-3 fs-12'>
                    {sectionMeta[s.id].desc}
                  </p>
                  {sectionRenderers[s.id]()}
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>

      <ConfirmModal
        show={deleteCargoRow !== null}
        title='Delete Cargo'
        message='Are you sure you want to remove this cargo from the list?'
        confirmLabel='Delete'
        confirmColor='danger'
        onConfirm={confirmDeleteCargo}
        onClose={() => setDeleteCargoRow(null)}
      />

      <ConfirmModal
        show={deleteCrude !== null}
        title='Delete Crude / Condensat'
        message={`Are you sure you want to remove "${deleteCrude?.row?.crudeName || ''}" from this cargo?`}
        confirmLabel='Delete'
        confirmColor='danger'
        onConfirm={confirmDeleteCrude}
        onClose={() => setDeleteCrude(null)}
      />

      <ConfirmModal
        show={deleteClauseRow !== null}
        title='Delete Clause'
        message={`Are you sure you want to remove clause "${deleteClauseRow?.title || ''}" from this Planning?`}
        confirmLabel='Delete'
        confirmColor='danger'
        onConfirm={confirmDeleteClause}
        onClose={() => setDeleteClauseRow(null)}
      />

      <Modal
        isOpen={existingClauseModal}
        toggle={() => setExistingClauseModal(false)}
        size='lg'
        centered
      >
        <ModalHeader toggle={() => setExistingClauseModal(false)}>
          <span className='d-block'>Add Existing Clause</span>
          <small className='text-muted fw-normal fs-12'>
            Panggil klausul eksisting dari Master Data (Clausa Information) yang
            belum ada di Planning ini
          </small>
        </ModalHeader>
        <ModalBody>
          <Input
            type='text'
            className='mb-3'
            placeholder='Cari kategori atau isi klausul eksisting...'
            value={clauseSearch}
            onChange={e => setClauseSearch(e.target.value)}
          />
          {(() => {
            const q = clauseSearch.trim().toLowerCase();
            const rows = clauseTemplates.filter(
              t => !q || (t.categoryName || '').toLowerCase().includes(q)
            );
            const allChecked =
              rows.length > 0 &&
              rows.every(t => clauseSelection[t.id]?.checked);
            const toggleRow = (id, patch) =>
              setClauseSelection(prev => ({
                ...prev,
                [id]: {
                  checked: prev[id]?.checked || false,
                  mandatory: prev[id]?.mandatory ?? true,
                  ...patch,
                },
              }));
            return (
              <div className='table-responsive'>
                <Table className='table-sm align-middle mb-0'>
                  <thead className='table-dark'>
                    <tr>
                      <th style={{ width: '36px' }}>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          checked={allChecked}
                          onChange={() =>
                            setClauseSelection(prev => {
                              const next = { ...prev };
                              rows.forEach(t => {
                                next[t.id] = {
                                  checked: !allChecked,
                                  mandatory: prev[t.id]?.mandatory ?? true,
                                };
                              });
                              return next;
                            })
                          }
                        />
                      </th>
                      <th>Nama Clause Category</th>
                      <th style={{ width: '140px' }}>Templates</th>
                      <th style={{ width: '130px' }}>
                        Registered Supplier Mandatory
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className='text-center text-muted py-3'>
                          Tidak ada klausul yang cocok
                        </td>
                      </tr>
                    ) : (
                      rows.map(t => (
                        <tr key={t.id}>
                          <td>
                            <Input
                              type='checkbox'
                              className='form-check-input'
                              checked={!!clauseSelection[t.id]?.checked}
                              onChange={e =>
                                toggleRow(t.id, { checked: e.target.checked })
                              }
                            />
                          </td>
                          <td>
                            <span className='fw-semibold d-block'>
                              {t.categoryName}
                            </span>
                            <span
                              className={`badge mt-1 ${
                                t.isActive
                                  ? 'bg-success-subtle text-success'
                                  : 'bg-warning-subtle text-warning'
                              }`}
                            >
                              {t.isActive ? 'Standard (Aktif)' : 'Non-Standard'}
                            </span>
                          </td>
                          <td>
                            <span className='badge bg-secondary-subtle text-secondary'>
                              {t.templateCount ?? 0} template
                            </span>
                          </td>
                          <td>
                            <Input
                              type='select'
                              bsSize='sm'
                              value={
                                clauseSelection[t.id]?.mandatory === false
                                  ? 'no'
                                  : 'yes'
                              }
                              onChange={e =>
                                toggleRow(t.id, {
                                  mandatory: e.target.value === 'yes',
                                })
                              }
                            >
                              <option value='yes'>Yes</option>
                              <option value='no'>No</option>
                            </Input>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter className='justify-content-between'>
          <span className='text-muted fs-13'>
            Telah dipilih: <strong>{selectedClauseItems.length}</strong> Clause
            Eksisting
          </span>
          <div className='d-flex gap-2'>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              disabled={busy}
              onClick={() => setExistingClauseModal(false)}
            >
              Batal
            </ActionButton>
            <ActionButton
              variant='primary'
              icon='ri-check-line'
              disabled={busy}
              onClick={handleAddExistingClauses}
            >
              Tambahkan Clause Selected ({selectedClauseItems.length})
            </ActionButton>
          </div>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={newClauseModal}
        toggle={() => setNewClauseModal(false)}
        centered
      >
        <ModalHeader toggle={() => setNewClauseModal(false)}>
          Add New Clause Information
        </ModalHeader>
        <ModalBody>
          <p className='text-muted fs-12 mb-2'>
            Title akan menjadi <strong>Clausa Category baru</strong> di Master
            Data (non-standard — tidak ikut auto-seed planning berikutnya,
            tersedia lewat &quot;Add Eksisting Clause&quot;).
          </p>
          <div className='mb-3'>
            <Label className='form-label'>
              Clause Title <span className='text-danger'>*</span>
            </Label>
            <Input
              type='text'
              maxLength={100}
              value={newClauseDraft.title}
              onChange={e =>
                setNewClauseDraft({ ...newClauseDraft, title: e.target.value })
              }
            />
          </div>
          <div className='form-check form-switch mb-3'>
            <Input
              type='checkbox'
              role='switch'
              className='form-check-input'
              id='new-clause-mandatory'
              checked={newClauseDraft.mandatory}
              onChange={e =>
                setNewClauseDraft({
                  ...newClauseDraft,
                  mandatory: e.target.checked,
                })
              }
            />
            <Label for='new-clause-mandatory' className='form-check-label'>
              Mandatory clause for Registered Supplier to fulfill
            </Label>
          </div>
          <Label className='form-label'>
            Content / Summary Note <span className='text-danger'>*</span>
          </Label>
          <Input
            type='textarea'
            rows={5}
            value={newClauseDraft.note}
            onChange={e =>
              setNewClauseDraft({ ...newClauseDraft, note: e.target.value })
            }
          />
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            disabled={busy}
            onClick={() => setNewClauseModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-add-line'
            disabled={busy}
            onClick={handleAddNewClause}
          >
            Add Clause
          </ActionButton>
        </ModalFooter>
      </Modal>

      <AddCrudeModal
        show={crudeModalIdx !== null}
        planningId={planningId}
        cargo={crudeModalIdx !== null ? req.cargos[crudeModalIdx] : null}
        existingIds={(req.cargos[crudeModalIdx]?.crudes || []).map(
          c => c.implementationCrudeId
        )}
        onClose={() => setCrudeModalIdx(null)}
        onOk={handleAddCrudes}
      />
    </div>
  );
};

export default RequirementTab;
